import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  DefaultTemplateTaskAutomationModel,
  WorkTemplateTaskModel,
} from "../../../../../factories/templates/models/template.model";
import { useAppSelector } from "../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import {
  AUTOMATION_CONDITION_KEY,
  useAddDefaultJobTaskAutomationMutation,
  useDeleteDefaultJobTaskAutomationMutation,
  useGetAutomationJobTaskConditionsQuery,
} from "../../../../../slices/AutomationSlice";
import {
  useActivateTemplateMutation,
  useAddSubtaskMutation,
  useAddTemplateMutation,
  useDeactivateTemplateMutation,
  useDeleteSubtaskMutation,
  useDeleteTemplateMutation,
  useLazyGetTemplateQuery,
  useReorderSubtaskMutation,
  useUpdateSubtaskMutation,
  useUpdateTemplateMutation,
} from "../../../../../slices/TemplatesSlice";
import { IUser } from "../../../../../types/types";
import { AutomationDraft } from "../../../../work/pages/automation/automation.constants";
import {
  getAutomationActionBodyValue,
  getAutomationConditionBodyValue,
} from "../../../../work/pages/automation/automation.helpers";
import { getAutomationSummaryText } from "../../../../work/pages/automation/automation.summary";
import { AutomationTarget } from "../../../../work/pages/automation/automation.types";
import { useAutomationLookups } from "../../../../work/pages/automation/useAutomationLookups";
import { serverErrorMessage } from "../../../workTypesV2/model/workTypeEditor.utils";
import {
  getFirstTemplateTaskAutomation,
  mapTemplateTaskAutomationToItem,
} from "../model/taskTemplateAutomation.mappers";
import {
  TaskTemplateEditorController,
  TaskTemplateFormData,
} from "../model/taskTemplateEditor.types";
import {
  createDraftSubtaskId,
  failedSubtasksText,
  findInvalidSubtaskDue,
  findInvalidSubtaskTitle,
  getSubtaskDuePayload,
  getTimeBudgetValue,
  isSubtaskChanged,
  LocalTemplateSubtask,
  mapTemplateTaskToLocalSubtask,
  PendingTemplateAutomation,
  reorderItems,
  shownSubtaskOrder,
  subtaskTitleProblem,
} from "../model/taskTemplateEditor.utils";

export const useTaskTemplateEditorController =
  (): TaskTemplateEditorController => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = useAppSelector((state) => state.appReducer.user);

    const isEditing = Boolean(id && id !== "add");

    const { control, handleSubmit, reset } = useForm<TaskTemplateFormData>({
      defaultValues: { templateName: "", description: "" },
    });

    const [tasks, setTasks] = useState<LocalTemplateSubtask[]>([]);
    const [initialTemplateTasks, setInitialTemplateTasks] = useState<
      WorkTemplateTaskModel[]
    >([]);
    const [draftSubtasks, setDraftSubtasks] = useState<LocalTemplateSubtask[]>(
      [],
    );
    const [isOpenAdd, setIsOpenAdd] = useState(false);
    const [editingSubtask, setEditingSubtask] =
      useState<LocalTemplateSubtask | null>(null);
    const [openTemplateDelete, setOpenTemplateDelete] = useState(false);
    const [openTemplateDeactivate, setOpenTemplateDeactivate] = useState(false);
    const [openTemplateActivate, setOpenTemplateActivate] = useState(false);
    const [selectedAutomationTarget, setSelectedAutomationTarget] =
      useState<AutomationTarget | null>(null);
    const [deletingAutomationIds, setDeletingAutomationIds] = useState<
      Set<string>
    >(() => new Set());
    const isAddingAutomationRef = useRef(false);

    const [
      getTemplate,
      { isLoading: isLoadingTemplate, data: loadedTemplate },
    ] = useLazyGetTemplateQuery();
    // A lazy query keeps the last template it loaded; when this one fails
    // (e.g. 404) the page showed the previous template's data (QA batch 16,
    // TPL-011). Only the template of this address is used.
    const template =
      loadedTemplate &&
      String(loadedTemplate.id).toLowerCase() === String(id).toLowerCase()
        ? loadedTemplate
        : undefined;
    const [updateTemplate, { isLoading: isLoadingUpdate }] =
      useUpdateTemplateMutation();
    const [addTemplate, { isLoading: isLoadingAdd }] = useAddTemplateMutation();
    const [addSubtask, { isLoading: isLoadingCreateSubtask }] =
      useAddSubtaskMutation();
    const [updateSubtask, { isLoading: isLoadingUpdateSubtask }] =
      useUpdateSubtaskMutation();
    const [deleteSubtask, { isLoading: isLoadingDeleteSubtask }] =
      useDeleteSubtaskMutation();
    const [deleteTemplate, { isLoading: isLoadingDelete }] =
      useDeleteTemplateMutation();
    const [deactivateTemplate, { isLoading: isLoadingDeactivate }] =
      useDeactivateTemplateMutation();
    const [activateTemplate, { isLoading: isLoadingActivate }] =
      useActivateTemplateMutation();
    const [reorderSubtasks] = useReorderSubtaskMutation();
    const [addDefaultJobTaskAutomation, { isLoading: isAddingAutomation }] =
      useAddDefaultJobTaskAutomationMutation();
    const [deleteDefaultJobTaskAutomation] =
      useDeleteDefaultJobTaskAutomationMutation();
    const { data: jobTaskConditions = [] } =
      useGetAutomationJobTaskConditionsQuery();
    const automationLookups = useAutomationLookups();

    const displayedSubtasks = isEditing ? tasks : draftSubtasks;
    const createdBy = useMemo(() => {
      const fullName = `${user?.userProfile?.firstName || ""} ${
        user?.userProfile?.lastName || ""
      }`.trim();

      return fullName || "Unknown user";
    }, [user?.userProfile?.firstName, user?.userProfile?.lastName]);

    const automationTooltipsBySubtaskId = useMemo(() => {
      const tooltips = new Map<string, string>();

      displayedSubtasks.forEach((subtask) => {
        const pending = subtask.pendingAutomation;
        const automation =
          subtask.defaultTemplateTaskAutomation?.[0] ??
          (pending
            ? ({
                id: `pending-${subtask.id}`,
                conditionId: pending.conditionId,
                actionId: pending.actionId,
                conditionValue: pending.conditionValue,
                actionValue: pending.actionValue,
                createdAt: new Date().toISOString(),
              } as unknown as DefaultTemplateTaskAutomationModel)
            : undefined);

        if (!automation) {
          return;
        }

        const automationItem = mapTemplateTaskAutomationToItem({
          automation,
          subtask,
          options: automationLookups.options,
          jobTaskConditions,
        });

        tooltips.set(
          subtask.id,
          getAutomationSummaryText({
            automation: automationItem,
            workName: template?.templateName || "Template",
            options: automationLookups.options,
          }),
        );
      });

      return tooltips;
    }, [
      automationLookups.options,
      jobTaskConditions,
      displayedSubtasks,
      template?.templateName,
    ]);

    const isSavingTemplate =
      isLoadingUpdate ||
      isLoadingAdd ||
      isLoadingCreateSubtask ||
      isLoadingUpdateSubtask ||
      isLoadingDeleteSubtask;

    const refetchTemplate = useCallback(async () => {
      if (isEditing && id) {
        await getTemplate({ id }).unwrap();
      }
    }, [getTemplate, id, isEditing]);

    const handleLocalSubtaskSave = useCallback(
      (subtask: {
        id?: string;
        name: string;
        description: string;
        budgetedTime?: string;
      }) => {
        const updateSubtasks = (prev: LocalTemplateSubtask[]) => {
          if (subtask.id) {
            return prev.map((item) =>
              item.id === subtask.id
                ? {
                    ...item,
                    name: subtask.name,
                    description: subtask.description,
                    budgetedTime:
                      subtask.budgetedTime || item.budgetedTime || "0",
                  }
                : item,
            );
          }

          return [
            ...prev,
            {
              id: createDraftSubtaskId(),
              name: subtask.name,
              description: subtask.description,
              budgetedTime: subtask.budgetedTime || "0",
              dueDays: "",
              dueAfterStart: true,
              isNew: isEditing,
            },
          ];
        };

        if (isEditing) {
          setTasks(updateSubtasks);
        } else {
          setDraftSubtasks(updateSubtasks);
        }

        setEditingSubtask(null);
        setIsOpenAdd(false);
      },
      [isEditing],
    );

    const handleEditDraftSubtask = useCallback(
      (subtask: LocalTemplateSubtask) => {
        setEditingSubtask(subtask);
        setIsOpenAdd(true);
      },
      [],
    );

    const handleDeleteDraftSubtask = useCallback(
      (subtaskId: string) => {
        if (isEditing) {
          setTasks((prev) => prev.filter((item) => item.id !== subtaskId));
          return;
        }

        setDraftSubtasks((prev) =>
          prev.filter((item) => item.id !== subtaskId),
        );
      },
      [isEditing],
    );

    const handleTemplateSubtaskSave = useCallback(
      (subtask: LocalTemplateSubtask) => {
        setTasks((prev) =>
          prev.map((item) =>
            item.id === subtask.id
              ? {
                  ...item,
                  name: subtask.name,
                  description: subtask.description,
                }
              : item,
          ),
        );
      },
      [],
    );

    const updateLocalSubtask = useCallback(
      (
        subtaskId: string,
        update: (subtask: LocalTemplateSubtask) => LocalTemplateSubtask,
      ) => {
        const apply = (prev: LocalTemplateSubtask[]) =>
          prev.map((subtask) =>
            subtask.id === subtaskId ? update(subtask) : subtask,
          );

        if (isEditing) {
          setTasks(apply);
        } else {
          setDraftSubtasks(apply);
        }
      },
      [isEditing],
    );

    const handleAssignUser = useCallback(
      (subtaskId: string, selectedUser: IUser) => {
        // Works for subtasks that aren't saved yet too: the assignee is sent
        // when the template is saved.
        const apply = (prev: LocalTemplateSubtask[]) =>
          prev.map((subtask) =>
            subtask.id === subtaskId
              ? {
                  ...subtask,
                  assignedUserId: selectedUser.id,
                  assignedUser: {
                    id: selectedUser.id,
                    userProfile: selectedUser.userProfile,
                  },
                }
              : subtask,
          );

        if (isEditing) {
          setTasks(apply);
        } else {
          setDraftSubtasks(apply);
        }
      },
      [isEditing],
    );

    const handleSubtaskBudgetChange = useCallback(
      (subtaskId: string, budgetedTime: string) => {
        const normalizedBudget = budgetedTime || "0";

        if (!isEditing) {
          setDraftSubtasks((prev) =>
            prev.map((subtask) =>
              subtask.id === subtaskId
                ? { ...subtask, budgetedTime: normalizedBudget }
                : subtask,
            ),
          );
          return;
        }

        setTasks((prev) =>
          prev.map((subtask) =>
            subtask.id === subtaskId
              ? { ...subtask, budgetedTime: normalizedBudget }
              : subtask,
          ),
        );
      },
      [isEditing],
    );

    const handleSubtaskDueChange = useCallback(
      (subtaskId: string, dueDays: string, dueAfterStart: boolean) => {
        updateLocalSubtask(subtaskId, (subtask) => ({
          ...subtask,
          dueDays,
          dueAfterStart,
        }));
      },
      [updateLocalSubtask],
    );

    const handleOpenAddSubtask = useCallback(() => {
      setEditingSubtask(null);
      setIsOpenAdd(true);
    }, []);

    const handleCloseSubtaskForm = useCallback(() => {
      setEditingSubtask(null);
      setIsOpenAdd(false);
    }, []);

    const handleOpenAutomationModal = useCallback(
      (subtask: WorkTemplateTaskModel) => {
        setSelectedAutomationTarget({
          type: "subtask",
          id: subtask.id,
          title: subtask.name,
        });
      },
      [],
    );

    const handleCloseAutomationModal = useCallback(() => {
      setSelectedAutomationTarget(null);
    }, []);

    const handleAddAutomation = useCallback(
      async (draft: AutomationDraft, target: AutomationTarget) => {
        if (
          isAddingAutomationRef.current ||
          !draft.conditionId ||
          !draft.actionId
        ) {
          return;
        }

        const orgId = getLinkedFirm()?.orgId || "";

        if (!orgId) {
          toast.error("Missing firm for automation");
          return;
        }

        const conditionValue = getAutomationConditionBodyValue({
          conditionKey: draft.conditionKey,
          conditionValue: draft.conditionValue,
          conditionExtraValue: draft.conditionExtraValue,
          workStatusOptions: automationLookups.options.workStatuses,
          subtaskStatusOptions: automationLookups.options.subtaskStatuses,
        });
        const actionValue = getAutomationActionBodyValue({
          actionKey: draft.actionKey,
          actionValue: draft.actionValue,
          actionExtraValue: draft.actionExtraValue,
          workStatusOptions: automationLookups.options.workStatuses,
          subtaskStatusOptions: automationLookups.options.subtaskStatuses,
        });
        const noOfDays =
          draft.conditionKey === AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE
            ? Number(draft.conditionValue)
            : undefined;

        const targetSubtask = displayedSubtasks.find(
          (subtask) => subtask.id === target.id,
        );

        if (targetSubtask && (!isEditing || targetSubtask.isNew)) {
          // Not saved yet: keep it with the subtask and create it on Save.
          const pendingAutomation: PendingTemplateAutomation = {
            conditionId: draft.conditionId,
            actionId: draft.actionId,
            conditionKey: draft.conditionKey,
            actionKey: draft.actionKey,
            conditionValue,
            actionValue,
            noOfDays: Number.isFinite(noOfDays) ? noOfDays : undefined,
          };
          updateLocalSubtask(target.id, (subtask) => ({
            ...subtask,
            pendingAutomation,
          }));
          setSelectedAutomationTarget(null);
          toast.success("Automation added. It will be saved with the template");
          return;
        }

        isAddingAutomationRef.current = true;

        try {
          await addDefaultJobTaskAutomation({
            orgId,
            templateTaskId: target.id,
            conditionId: draft.conditionId,
            actionId: draft.actionId,
            isActive: true,
            conditionValue,
            actionValue,
            noOfDays: Number.isFinite(noOfDays) ? noOfDays : undefined,
          }).unwrap();
          await refetchTemplate();
          setSelectedAutomationTarget(null);
          toast.success("Automation created successfully");
        } catch {
          toast.error("Failed to create automation");
        } finally {
          isAddingAutomationRef.current = false;
        }
      },
      [
        addDefaultJobTaskAutomation,
        automationLookups.options.subtaskStatuses,
        automationLookups.options.workStatuses,
        displayedSubtasks,
        isEditing,
        refetchTemplate,
        updateLocalSubtask,
      ],
    );

    const handleDeleteAutomation = useCallback(
      async (subtask: WorkTemplateTaskModel) => {
        const local = displayedSubtasks.find((item) => item.id === subtask.id);

        if (local?.pendingAutomation) {
          updateLocalSubtask(subtask.id, (item) => ({
            ...item,
            pendingAutomation: undefined,
          }));
          return;
        }

        const automation = getFirstTemplateTaskAutomation(subtask);

        if (!automation || deletingAutomationIds.has(automation.id)) {
          return;
        }

        setDeletingAutomationIds((prev) => new Set(prev).add(automation.id));

        try {
          await deleteDefaultJobTaskAutomation(automation.id).unwrap();
          await refetchTemplate();
          toast.success("Automation deleted successfully");
        } catch {
          toast.error("Failed to delete automation");
        } finally {
          setDeletingAutomationIds((prev) => {
            const next = new Set(prev);

            next.delete(automation.id);
            return next;
          });
        }
      },
      [
        deleteDefaultJobTaskAutomation,
        deletingAutomationIds,
        displayedSubtasks,
        refetchTemplate,
        updateLocalSubtask,
      ],
    );

    /** Adds a new subtask with its assignee, then its automation (if any). */
    const saveNewSubtask = useCallback(
      async (subtask: LocalTemplateSubtask, workTemplateId: string) => {
        const created = (await addSubtask({
          name: subtask.name,
          description: subtask.description,
          workTemplateId,
          timeBudget: getTimeBudgetValue(subtask.budgetedTime),
          assignedUserId: subtask.assignedUserId,
          ...getSubtaskDuePayload(subtask),
        }).unwrap()) as { id?: string } | undefined;
        const pending = subtask.pendingAutomation;
        const orgId = getLinkedFirm()?.orgId || "";

        let automationFailed = false;

        if (pending && created?.id && orgId) {
          try {
            await addDefaultJobTaskAutomation({
              orgId,
              templateTaskId: created.id,
              conditionId: pending.conditionId,
              actionId: pending.actionId,
              isActive: true,
              conditionValue: pending.conditionValue as never,
              actionValue: pending.actionValue as never,
              noOfDays: pending.noOfDays,
            }).unwrap();
          } catch {
            automationFailed = true;
          }
        }

        return { id: created?.id, automationFailed };
      },
      [addDefaultJobTaskAutomation, addSubtask],
    );

    /**
     * Adds the new subtasks one by one in the order shown. A subtask the
     * server refuses doesn't stop the others; the ones that failed are named
     * in one message afterwards (QA 2026-10-21: before, the first error
     * silently skipped every later subtask).
     */
    const saveNewSubtasks = useCallback(
      async (subtasks: LocalTemplateSubtask[], workTemplateId: string) => {
        const createdIds = new Map<string, string>();
        const failed: string[] = [];
        let firstError = "";
        let automationFailures = 0;

        for (const subtask of subtasks) {
          try {
            const result = await saveNewSubtask(subtask, workTemplateId);
            if (result.id) createdIds.set(subtask.id, result.id);
            if (result.automationFailed) automationFailures += 1;
          } catch (e) {
            failed.push(subtask.name);
            if (!firstError) firstError = serverErrorMessage(e, "");
          }
        }

        if (failed.length) {
          toast(
            `${failedSubtasksText(failed)}${
              firstError ? ` ${firstError}` : ""
            }`,
            { type: "warning", autoClose: 10000 },
          );
        }
        if (automationFailures) {
          toast(
            `${automationFailures} subtask automation${
              automationFailures === 1 ? " was" : "s were"
            } not saved. Open the subtask to add it again.`,
            { type: "warning" },
          );
        }

        return { createdIds, failedCount: failed.length };
      },
      [saveNewSubtask],
    );

    const onSubmit = useCallback(
      async (formData: TaskTemplateFormData) => {
        const templateName = formData.templateName.trim();
        const description = formData.description.trim();

        if (!templateName) {
          toast("Template title is required", { type: "error" });
          return;
        }

        const invalidDue = findInvalidSubtaskDue(
          isEditing ? tasks : draftSubtasks,
        );

        if (invalidDue) {
          toast(
            `Subtask "${invalidDue.name}": due days must be a whole number from 0 to 365 (or empty).`,
            { type: "error" },
          );
          return;
        }

        const invalidTitle = findInvalidSubtaskTitle(
          isEditing ? tasks : draftSubtasks,
        );

        if (invalidTitle) {
          toast(
            `Subtask "${invalidTitle.name.slice(0, 40)}${
              invalidTitle.name.length > 40 ? "…" : ""
            }": ${subtaskTitleProblem(invalidTitle.name).toLowerCase()}.`,
            { type: "error" },
          );
          return;
        }

        if (template && isEditing) {
          try {
            const normalizedDescription = description || null;
            const isTemplateChanged =
              template.templateName !== templateName ||
              (template.description || null) !== normalizedDescription;

            const originalSubtasksById = new Map(
              initialTemplateTasks.map((subtask) => [subtask.id, subtask]),
            );
            const currentExistingSubtasks = tasks.filter(
              (subtask) => !subtask.isNew,
            );
            const deletedSubtasks = initialTemplateTasks.filter(
              (subtask) =>
                !currentExistingSubtasks.some((item) => item.id === subtask.id),
            );
            const newSubtasks = tasks.filter((subtask) => subtask.isNew);
            const changedSubtasks = currentExistingSubtasks.filter(
              (subtask) => {
                const original = originalSubtasksById.get(subtask.id);

                return original ? isSubtaskChanged(original, subtask) : false;
              },
            );
            const originalOrder = initialTemplateTasks
              .map((subtask) => subtask.id)
              .join(",");
            const currentExistingOrder = currentExistingSubtasks
              .map((subtask) => subtask.id)
              .join(",");
            const isOrderChanged = originalOrder !== currentExistingOrder;

            if (isTemplateChanged) {
              await updateTemplate({
                ...template,
                templateName,
                description: normalizedDescription,
              }).unwrap();
            }

            for (const subtask of deletedSubtasks) {
              await deleteSubtask(subtask).unwrap();
            }

            for (const subtask of changedSubtasks) {
              const original = originalSubtasksById.get(subtask.id);

              if (!original) {
                continue;
              }

              await updateSubtask({
                ...original,
                name: subtask.name,
                description: subtask.description,
                assignedUserId: subtask.assignedUserId,
                assignedUser: subtask.assignedUser ?? original.assignedUser,
                timeBudget: getTimeBudgetValue(subtask.budgetedTime),
                ...getSubtaskDuePayload(subtask),
              }).unwrap();
            }

            const { createdIds, failedCount } = await saveNewSubtasks(
              newSubtasks,
              template.id,
            );

            // New subtasks are added at the end on the server, so when any
            // were added (or saved ones moved) the order shown is sent too.
            const shownOrder = shownSubtaskOrder(tasks, createdIds);
            const isShownOrderNew =
              shownOrder.join(",") !==
              [
                ...currentExistingSubtasks.map((subtask) => subtask.id),
                ...newSubtasks
                  .map((subtask) => createdIds.get(subtask.id))
                  .filter(Boolean),
              ].join(",");

            if (isOrderChanged || isShownOrderNew) {
              await reorderSubtasks(shownOrder).unwrap();
            }

            if (!failedCount) {
              toast("Template successfully updated", {
                type: "success",
              });
            }

            navigate(-1);
          } catch (e) {
            toast(serverErrorMessage(e, "Something went wrong"), {
              type: "error",
            });
          }

          return;
        }

        try {
          const orgId = getLinkedFirm()?.orgId;

          if (!orgId) {
            toast("Missing firm for template", {
              type: "error",
            });
            return;
          }

          const createdTemplate = await addTemplate({
            templateName,
            description,
            isActive: true,
            workTemplateTasks: [],
            orgId: null,
            orgRelayId: orgId,
          }).unwrap();

          if (draftSubtasks.length > 0) {
            // Added one by one in the order shown (the server appends each
            // at the end), so the order on screen is kept.
            const { failedCount } = await saveNewSubtasks(
              draftSubtasks,
              createdTemplate.id,
            );

            if (failedCount) {
              navigate("/settings/templates/task-templates");
              return;
            }
          }

          toast("Template successfully created", {
            type: "success",
          });

          navigate("/settings/templates/task-templates");
        } catch (e) {
          toast(serverErrorMessage(e, "Something went wrong"), {
            type: "error",
          });
        }
      },
      [
        addTemplate,
        deleteSubtask,
        draftSubtasks,
        initialTemplateTasks,
        isEditing,
        navigate,
        reorderSubtasks,
        saveNewSubtasks,
        tasks,
        template,
        updateSubtask,
        updateTemplate,
      ],
    );

    const onDragEnd = useCallback(
      async (result: DropResult) => {
        const { destination, source } = result;

        if (!destination || source.index === destination.index) {
          return;
        }

        if (isEditing) {
          setTasks((prev) =>
            reorderItems(prev, source.index, destination.index),
          );
          return;
        }

        setDraftSubtasks((prev) =>
          reorderItems(prev, source.index, destination.index),
        );
      },
      [isEditing],
    );

    const handleDeleteTemplate = useCallback(async () => {
      if (!template) {
        return;
      }

      try {
        await deleteTemplate(template).unwrap();

        toast("Template successfully deleted", {
          type: "success",
        });

        setOpenTemplateDelete(false);
        navigate("/settings/templates/task-templates");
      } catch {
        toast(
          "Something went wrong. You can deactivate the template to hide it",
          {
            type: "error",
          },
        );
      }
    }, [deleteTemplate, navigate, template]);

    const handleDeactivateTemplate = useCallback(async () => {
      if (!template) {
        return;
      }

      try {
        await deactivateTemplate(template).unwrap();

        toast("Template successfully deactivated", {
          type: "success",
        });

        setOpenTemplateDeactivate(false);
        await refetchTemplate();
      } catch (e) {
        toast(serverErrorMessage(e, "Something went wrong"), {
          type: "error",
        });
      }
    }, [deactivateTemplate, refetchTemplate, template]);

    const handleActivateTemplate = useCallback(async () => {
      if (!template) {
        return;
      }

      try {
        await activateTemplate(template).unwrap();

        toast("Template successfully activated", {
          type: "success",
        });

        setOpenTemplateActivate(false);
        await refetchTemplate();
      } catch (e) {
        toast(serverErrorMessage(e, "Something went wrong"), {
          type: "error",
        });
      }
    }, [activateTemplate, refetchTemplate, template]);

    useEffect(() => {
      if (isEditing && id) {
        void getTemplate({ id });
        return;
      }

      setTasks([]);
      setInitialTemplateTasks([]);
      reset({
        templateName: "",
        description: "",
      });
    }, [getTemplate, id, isEditing, reset]);

    useEffect(() => {
      if (!template) {
        return;
      }

      setInitialTemplateTasks(template.workTemplateTasks || []);
      setTasks(
        (template.workTemplateTasks || []).map(mapTemplateTaskToLocalSubtask),
      );
      reset({
        templateName: template.templateName || "",
        description: template.description || "",
      });
    }, [reset, template]);

    return {
      isLoading: isLoadingTemplate,
      headerProps: {
        title: isEditing
          ? template?.templateName || "Unknown"
          : "Add new template",
        isEditing,
        isSaving: isSavingTemplate,
        canShowActions: isEditing && Boolean(template),
        isActive: Boolean(template?.isActive),
        onBack: () => navigate("/settings/templates/task-templates"),
        onCancel: () => navigate(-1),
        onSave: () => handleSubmit(onSubmit)(),
        onToggleActive: () => {
          if (template?.isActive) {
            setOpenTemplateDeactivate(true);
          } else {
            setOpenTemplateActivate(true);
          }
        },
        onDelete: () => setOpenTemplateDelete(true),
      },
      detailsProps: {
        control,
        isEditing,
        template,
        onActiveChange: (value) => {
          if (value) {
            setOpenTemplateActivate(true);
          } else {
            setOpenTemplateDeactivate(true);
          }
        },
      },
      subtasksProps: {
        isEditing,
        subtasks: displayedSubtasks,
        isOpenAdd,
        editingSubtask,
        automationTooltipsBySubtaskId,
        deletingAutomationIds,
        onDragEnd,
        onOpenAddSubtask: handleOpenAddSubtask,
        onCloseSubtaskForm: handleCloseSubtaskForm,
        onSaveLocalSubtask: handleLocalSubtaskSave,
        onEditDraftSubtask: handleEditDraftSubtask,
        onDeleteDraftSubtask: handleDeleteDraftSubtask,
        onEditTemplateSubtask: handleTemplateSubtaskSave,
        onAssignUser: handleAssignUser,
        onBudgetChange: handleSubtaskBudgetChange,
        onDueChange: handleSubtaskDueChange,
        onAddAutomation: handleOpenAutomationModal,
        onDeleteAutomation: handleDeleteAutomation,
      },
      modalProps: {
        template,
        isDeleteOpen: openTemplateDelete,
        isDeactivateOpen: openTemplateDeactivate,
        isActivateOpen: openTemplateActivate,
        isDeleting: isLoadingDelete,
        isDeactivating: isLoadingDeactivate,
        isActivating: isLoadingActivate,
        onCloseDelete: () => setOpenTemplateDelete(false),
        onCloseDeactivate: () => setOpenTemplateDeactivate(false),
        onCloseActivate: () => setOpenTemplateActivate(false),
        onConfirmDelete: () => void handleDeleteTemplate(),
        onConfirmDeactivate: () => void handleDeactivateTemplate(),
        onConfirmActivate: () => void handleActivateTemplate(),
      },
      automationModalProps: {
        isOpen: Boolean(selectedAutomationTarget),
        target: selectedAutomationTarget,
        createdBy,
        lookups: automationLookups,
        isSaving: isAddingAutomation,
        onClose: handleCloseAutomationModal,
        onSave: handleAddAutomation,
      },
    };
  };
